import React from 'react';
import {
  Table,
  Text,
  Group,
  Stack,
  Badge,
  ActionIcon,
  Tooltip,
  Pagination,
  Center
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconExternalLink,
  IconChevronUp,
  IconChevronDown,
  IconSelector
} from '@tabler/icons-react';
import { MentionListProps } from './MentionList.types';
import { RedditMentionDto } from '../../../../domain/entities/RedditMention';
import { EmotionalToneBadge } from '../EmotionalToneBadge';
import { ConfidenceIndicator } from '../ConfidenceIndicator';
import { TableSkeleton } from '../../common/TableSkeleton';
import { appendRedditContext } from '@/utils/redditUtils';

/**
 * MentionList Component
 *
 * Displays a table of Reddit mentions with sorting, pagination, and actions
 *
 * Features:
 * - Sortable columns (date, confidence, subreddit)
 * - Pagination support
 * - Row click navigation
 * - Action buttons (confirm, dismiss)
 * - Loading skeleton
 * - Empty state
 * - Responsive design
 *
 * Usage:
 * ```tsx
 * <MentionList
 *   mentions={mentions}
 *   loading={isLoading}
 *   onMentionClick={handleClick}
 *   onPageChange={handlePageChange}
 *   totalPages={10}
 * />
 * ```
 */
export const MentionList: React.FC<MentionListProps> = ({
  mentions,
  loading = false,
  error = null,
  onMentionClick,
  onConfirm,
  onDismiss,
  currentPage = 1,
  totalPages = 1,
  totalItems,
  onPageChange,
  sortBy = 'createdAt',
  sortDirection = 'desc',
  onSortChange,
  showActions = true
}): React.ReactElement => {
  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status: string): React.ReactElement => {
    switch (status) {
      case 'Verified':
        return (
          <Badge color="green" variant="light" size="sm">
            Verified
          </Badge>
        );
      case 'Rejected':
        return (
          <Badge color="gray" variant="light" size="sm">
            Rejected
          </Badge>
        );
      case 'Matched':
        return (
          <Badge color="blue" variant="light" size="sm">
            Matched
          </Badge>
        );
      default:
        return (
          <Badge color="orange" variant="light" size="sm">
            Unmatched
          </Badge>
        );
    }
  };

  // Handle sort click
  const handleSortClick = (field: string): void => {
    if (!onSortChange) {return;}

    if (sortBy === field) {
      // Toggle direction
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      onSortChange(field, newDirection);
    } else {
      // New field, default to descending
      onSortChange(field, 'desc');
    }
  };

  // Get sort icon
  const getSortIcon = (field: string): React.ReactElement => {
    if (sortBy !== field) {
      return <IconSelector size={14} />;
    }
    return sortDirection === 'asc' ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />;
  };

  // Sortable column header
  const SortableHeader: React.FC<{ field: string; children: React.ReactNode }> = ({
    field,
    children
  }) => (
    <Table.Th onClick={() => handleSortClick(field)}>
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" fw={600}>
          {children}
        </Text>
        {onSortChange && getSortIcon(field)}
      </Group>
    </Table.Th>
  );

  // Handle row click
  const handleRowClick = (mention: RedditMentionDto): void => {
    if (onMentionClick) {
      onMentionClick(mention);
    }
  };

  // Show loading skeleton
  if (loading) {
    return <TableSkeleton rows={5} columns={showActions ? 7 : 6} />;
  }

  // Show error state
  if (error) {
    return (
      <Center p="xl">
        <Stack align="center" gap="md">
          <Text size="lg" c="red" fw={600}>
            Error loading mentions
          </Text>
          <Text size="sm" c="dimmed">
            {error}
          </Text>
        </Stack>
      </Center>
    );
  }

  // Show empty state
  if (mentions.length === 0) {
    return (
      <Center p="xl">
        <Stack align="center" gap="md">
          <Text size="lg" fw={600} c="dimmed">
            No mentions found
          </Text>
          <Text size="sm" c="dimmed">
            There are no mentions to display.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Stack gap="md">
      {/* Total count */}
      {totalItems !== undefined && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            Showing {mentions.length} of {totalItems} mention{totalItems !== 1 ? 's' : ''}
          </Text>
        </Group>
      )}

      {/* Table */}
      <div>
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <SortableHeader field="postTitle">Post Title</SortableHeader>
              <SortableHeader field="subreddit">Subreddit</SortableHeader>
              <Table.Th>
                <Text size="sm" fw={600}>
                  Business
                </Text>
              </Table.Th>
              <SortableHeader field="matchConfidenceScore">Confidence</SortableHeader>
              <SortableHeader field="emotionalTone">Tone</SortableHeader>
              <Table.Th>
                <Text size="sm" fw={600}>
                  Status
                </Text>
              </Table.Th>
              <SortableHeader field="createdAt">Date</SortableHeader>
              {showActions && (
                <Table.Th>
                  <Text size="sm" fw={600}>
                    Actions
                  </Text>
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {mentions.map((mention) => (
              <Table.Tr key={mention.id} onClick={() => handleRowClick(mention)}>
                {/* Post Title */}
                <Table.Td>
                  <Text size="sm" lineClamp={2}>
                    {mention.postTitle}
                  </Text>
                </Table.Td>

                {/* Subreddit */}
                <Table.Td>
                  <Text size="sm" c="blue" fw={500}>
                    r/{mention.subreddit}
                  </Text>
                </Table.Td>

                {/* Business */}
                <Table.Td>
                  {mention.businessName ? (
                    <Text size="sm">{mention.businessName}</Text>
                  ) : (
                    <Text size="sm" c="dimmed" fs="italic">
                      No match
                    </Text>
                  )}
                </Table.Td>

                {/* Confidence */}
                <Table.Td>
                  <ConfidenceIndicator
                    score={mention.matchConfidenceScore ?? 0}
                    variant="badge"
                    size="sm"
                    showPercentage
                    showLabel={false}
                  />
                </Table.Td>

                {/* Emotional Tone */}
                <Table.Td>
                  {mention.emotionalTone ? (
                    <EmotionalToneBadge tone={mention.emotionalTone} size="sm" />
                  ) : (
                    <Text size="sm" c="dimmed">
                      —
                    </Text>
                  )}
                </Table.Td>

                {/* Status */}
                <Table.Td>{getStatusBadge(mention.matchStatus)}</Table.Td>

                {/* Date */}
                <Table.Td>
                  <Text size="sm">{formatDate(mention.createdAt)}</Text>
                </Table.Td>

                {/* Actions */}
                {showActions && (
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Group gap="xs" wrap="nowrap">
                      {/* View on Reddit */}
                      <Tooltip label="View on Reddit">
                        <ActionIcon
                          variant="subtle"
                          color="blue"
                          size="sm"
                          component="a"
                          href={appendRedditContext(mention.postPermalink)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      </Tooltip>

                      {/* Confirm (only for Matched status) */}
                      {mention.matchStatus === 'Matched' && mention.businessId && onConfirm && (
                        <Tooltip label="Confirm Match">
                          <ActionIcon
                            variant="subtle"
                            color="green"
                            size="sm"
                            onClick={() => {
                              if (mention.businessId) {
                                onConfirm(mention.id, mention.businessId);
                              }
                            }}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}

                      {/* Dismiss (only for Matched status) */}
                      {mention.matchStatus === 'Matched' && onDismiss && (
                        <Tooltip label="Dismiss">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            size="sm"
                            onClick={() => onDismiss(mention.id)}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                )}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && onPageChange && (
        <Center>
          <Pagination
            total={totalPages}
            value={currentPage}
            onChange={onPageChange}
            size="sm"
            withEdges
          />
        </Center>
      )}
    </Stack>
  );
};
